<template>
    <ComboboxRoot
        v-model:open="isOpen"
        :ignore-filter="true"
        :open-on-click="true"
        :open-on-focus="true"
        :reset-search-term-on-select="false"
        class="relative"
        :class="customClass"
    >
        <ComboboxAnchor
            class="flex h-input items-center gap-2 rounded-input border border-muval-gray-4 bg-white px-3 focus-within:border-muval-gray-1"
        >
            <ComboboxInput
                v-model="query"
                :placeholder="placeholder"
                class="w-full bg-transparent text-sm text-muval-gray-1 placeholder:text-muval-gray-3 focus:outline-none"
            />
            <SvgIcon
                v-if="isLoading"
                name="spinner"
                class="size-4 shrink-0 animate-spin text-muval-gray-3"
            />
            <SvgIcon
                v-else
                name="system-chevron-down"
                class="size-[18px] shrink-0 text-muval-gray-1 transition-transform duration-300"
                :class="{ 'rotate-180': isOpen }"
            />
        </ComboboxAnchor>

        <!--
            Portalled to the body deliberately. The list is absolutely positioned,
            so it never grows its container, and inside a MuvalModal the panel
            computes to `overflow-y: auto` — `overflow-x: hidden` forces the
            `overflow-y-visible` it is authored with to compute to `auto` — which
            clipped the options at the panel edge and scrolled the dialog
            (CU-86d48abuv). A portal puts the list outside every ancestor's
            overflow, which is how MPDropdownContent and PriceItemSearch solve it.
        -->
        <ComboboxPortal>
            <ComboboxContent
                position="popper"
                :side-offset="4"
                class="z-[100] w-[var(--reka-combobox-trigger-width)] rounded-muval-1 border border-muval-gray-4 bg-white shadow-muval-3"
            >
                <ComboboxViewport class="max-h-72 overflow-y-auto p-1">
                    <div
                        v-if="isLoading && orders.length === 0"
                        class="px-3 py-4 text-center text-sm text-muval-gray-3"
                    >
                        Searching orders...
                    </div>

                    <template v-else>
                        <ComboboxItem
                            v-for="order in orders"
                            :key="order.id"
                            :value="String(order.id)"
                            :text-value="formatOrderLabel(order)"
                            class="flex cursor-pointer select-none items-center rounded-[4px] px-3 py-2 text-sm leading-tight outline-none data-[highlighted]:bg-muval-gray-5 data-[state=checked]:font-semibold"
                            @select="onSelect(order)"
                        >
                            <span class="truncate text-muval-gray-1">{{ formatOrderLabel(order) }}</span>
                        </ComboboxItem>

                        <div
                            v-if="orders.length === 0"
                            class="px-3 py-4 text-center text-sm text-muval-gray-3"
                        >
                            {{ trimmedQuery ? `No orders match "${trimmedQuery}".` : 'No orders found.' }}
                        </div>
                    </template>
                </ComboboxViewport>
            </ComboboxContent>
        </ComboboxPortal>
    </ComboboxRoot>

    <div
        v-if="errorMessage || fetchError"
        class="mt-3 text-sm text-red-500"
    >
        {{ errorMessage || fetchError }}
    </div>
</template>

<script setup lang="ts">
import {
    ComboboxAnchor,
    ComboboxContent,
    ComboboxInput,
    ComboboxItem,
    ComboboxPortal,
    ComboboxRoot,
    ComboboxViewport,
} from 'reka-ui';
import { watchDebounced } from '@vueuse/core';
import { useTimesheetStore } from '@store/timesheet';

interface OrderOption {
    id: number;
    ref: string;
    customer_name: string;
    route: string;
}

const props = defineProps({
    vid: {
        type: String,
        required: true,
    },
    class: {
        type: String,
        required: false,
        default: '',
    },
    placeholder: {
        type: String,
        required: false,
        default: 'Select an order',
    },
    initialOrderId: {
        type: Number,
        required: false,
        default: null,
    },
});

const timesheet = useTimesheetStore();

const vid = toRef(props, 'vid');
const { value: inputValue, errorMessage } = useField<number | null>(vid, undefined, {
    syncVModel: true,
});

const orders = ref<OrderOption[]>([]);
const isLoading = ref(false);
const isOpen = ref(false);
const query = ref('');
const fetchError = ref('');

// Request tracking for concurrency safety
let lastRequestId = 0;

// Custom class handling
const customClass = computed(() => {
    return props.class || 'max-w-xs'; // Default width if no class provided
});

const trimmedQuery = computed(() => query.value.trim());

const formatOrderLabel = (order: OrderOption) => {
    return `#${order.ref} — ${order.customer_name}`;
};

// Held, not derived from `orders`: a search replaces the whole list, so a label
// computed from it blanks the input as soon as a search misses the selected
// order — while the field still holds that order.
const selectedOrder = ref<OrderOption | null>(null);

// Keeps the held order in step with the field, which is also written from
// outside (`initialOrderId`, a form reset). A list that cannot name the current
// id leaves an already-matching selection alone.
watch(
    [inputValue, orders],
    ([id, list]) => {
        if (!id) {
            selectedOrder.value = null;

            return;
        }

        const match = list.find((order) => order.id === id);

        if (match) {
            selectedOrder.value = match;
        } else if (selectedOrder.value?.id !== id) {
            selectedOrder.value = null;
        }
    },
    { immediate: true },
);

const selectedLabel = computed(() => (selectedOrder.value ? formatOrderLabel(selectedOrder.value) : ''));

const onSelect = (order: OrderOption) => {
    selectedOrder.value = order;
    inputValue.value = order.id;
    query.value = formatOrderLabel(order);
    isOpen.value = false;
};

// The input doubles as the search box and as the display of what is selected, so
// an abandoned search term must not linger next to a selection it does not match.
watch(isOpen, (open) => {
    if (!open) {
        query.value = selectedLabel.value;
    }
});

// Also covers `initialOrderId`: the field arrives populated but the label is only
// resolvable once the matching order has been fetched.
watch(
    [selectedLabel, isOpen],
    ([label, open]) => {
        if (label && !open && query.value !== label) {
            query.value = label;
        }
    },
    { immediate: true },
);

watchDebounced(
    query,
    async (value) => {
        // Selecting an option writes its label into the input. Searching for that
        // label would replace the list with whatever it happens to match.
        if (value === selectedLabel.value) {
            return;
        }

        await fetchOrders(value.trim());
    },
    {
        debounce: 500,
    },
);

onMounted(async () => {
    await fetchOrders('', props.initialOrderId ? [props.initialOrderId] : []);
});

const fetchOrders = async (query?: string, includeIds: number[] = []) => {
    fetchError.value = '';
    isLoading.value = true;

    const requestId = ++lastRequestId;

    try {
        const searchParams: any = {};
        if (query && query.length > 0) searchParams.keyword = query;
        if (includeIds.length > 0) searchParams.include_ids = includeIds;

        const resp = await timesheet.searchOrder(searchParams);

        if (requestId !== lastRequestId) return;

        const list = resp?.data?.data ?? [];
        orders.value = Array.isArray(list) ? list : [];
    } catch (error: any) {
        if (requestId !== lastRequestId) return;

        fetchError.value = error?.response?.data?.message ?? 'Search failed. Please try again.';
        orders.value = [];
    } finally {
        if (requestId === lastRequestId) {
            isLoading.value = false;
        }
    }
};
</script>
